import React, { useEffect, useState } from "react";
import { useConfirm } from "../../components/ConfirmDialog.jsx";
import "./ResidentVisitorApprovals.css";

export default function ResidentVisitorApprovals() {
  const confirm = useConfirm();
  const [pendingVisitors, setPendingVisitors] = useState([]);
  const [visitorHistory, setVisitorHistory] = useState([]);
  const [preApprovedVisitors, setPreApprovedVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [processing, setProcessing] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState({});
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [showPreApprovalForm, setShowPreApprovalForm] = useState(false);
  const [preApprovalForm, setPreApprovalForm] = useState({
    visitorName: "",
    visitorEmail: "",
    visitorPhone: "",
    purpose: "",
    expectedDate: "",
    validUntilDate: ""
  });

  // Decode resident ID from JWT token
  const residentId = JSON.parse(
    atob((localStorage.getItem("residentToken") || "").split(".")[1] || "e30=") || "{}"
  ).id;
  const residentToken = localStorage.getItem("residentToken");

  useEffect(() => {
    if (!residentToken) {
      setError("Not authenticated. Please log in.");
      setLoading(false);
      return;
    }
    loadVisitors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVisitors = async () => {
    try {
      setLoading(true);
      setError("");

      const API = process.env.REACT_APP_API_URL;

      // Fetch pending approvals
      const pendingRes = await fetch(
        `${API}/api/visitors/pending-approvals/${residentId}`,
        {
          headers: { Authorization: `Bearer ${residentToken}` }
        }
      );

      if (!pendingRes.ok) throw new Error("Failed to fetch pending visitors");
      const pendingData = await pendingRes.json();
      setPendingVisitors(pendingData.visitors || []);

      // Fetch visitor history
      const historyRes = await fetch(`${API}/api/visitors/history/${residentId}?days=30`, {
        headers: { Authorization: `Bearer ${residentToken}` }
      });

      if (!historyRes.ok) throw new Error("Failed to fetch visitor history");
      const historyData = await historyRes.json();
      setVisitorHistory(historyData.visitors || []);

      // Fetch pre-approved visitors
      const preApprovedRes = await fetch(
        `${API}/api/visitors/pre-approved/${residentId}`,
        {
          headers: { Authorization: `Bearer ${residentToken}` }
        }
      );

      if (!preApprovedRes.ok) {
        console.log("Pre-approved visitors not available yet");
        setPreApprovedVisitors([]);
      } else {
        const preApprovedData = await preApprovedRes.json();
        setPreApprovedVisitors(preApprovedData.visitors || []);
      }

      setLoading(false);
    } catch (err) {
      setError("Failed to load visitors: " + err.message);
      setLoading(false);
    }
  };

  const handleApprove = async (visitorId) => {
    try {
      setProcessing(visitorId);
      setError("");

      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/visitors/${visitorId}/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${residentToken}`
        },
        body: JSON.stringify({
          approvalNotes: approvalNotes[visitorId] || ""
        })
      });

      if (!response.ok) throw new Error("Failed to approve visitor");

      setSuccess("✅ Visitor approved!");
      setTimeout(() => setSuccess(""), 3000);

      // Reload visitors
      loadVisitors();
      setApprovalNotes({});
    } catch (err) {
      setError("Error approving visitor: " + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (visitorId) => {
    try {
      setProcessing(visitorId);
      setError("");

      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/visitors/${visitorId}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${residentToken}`
        },
        body: JSON.stringify({
          rejectionReason: rejectionReasons[visitorId] || "Not specified"
        })
      });

      if (!response.ok) throw new Error("Failed to reject visitor");

      setSuccess("❌ Visitor rejected");
      setTimeout(() => setSuccess(""), 3000);

      // Reload visitors
      loadVisitors();
      setRejectionReasons({});
    } catch (err) {
      setError("Error rejecting visitor: " + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN");
  };

  const formatDateOnly = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const formatDuration = (checkIn, checkOut) => {
    if (!checkIn) return "Not checked in";
    if (!checkOut) return "Still inside";

    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins} minutes`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const handleAddPreApproval = async (e) => {
    e.preventDefault();

    if (!preApprovalForm.visitorName || !preApprovalForm.expectedDate) {
      setError("Please enter visitor name and expected date");
      return;
    }

    try {
      setProcessing("adding");
      setError("");

      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/visitors/pre-approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${residentToken}`
        },
        body: JSON.stringify({
          visitorName: preApprovalForm.visitorName,
          visitorEmail: preApprovalForm.visitorEmail,
          visitorPhone: preApprovalForm.visitorPhone,
          purpose: preApprovalForm.purpose,
          expectedDate: preApprovalForm.expectedDate,
          validUntilDate: preApprovalForm.validUntilDate || preApprovalForm.expectedDate
        })
      });

      if (!response.ok) throw new Error("Failed to add pre-approval");

      setSuccess("✅ Visitor pre-approved!");
      setTimeout(() => setSuccess(""), 3000);

      // Reset form and reload
      setPreApprovalForm({
        visitorName: "",
        visitorEmail: "",
        visitorPhone: "",
        purpose: "",
        expectedDate: "",
        validUntilDate: ""
      });
      setShowPreApprovalForm(false);
      loadVisitors();
    } catch (err) {
      setError("Error adding pre-approval: " + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleRemovePreApproval = async (preApprovalId) => {
    const ok = await confirm({
      title: "Remove this pre-approval?",
      message: "The visitor will no longer be able to check in without a fresh approval.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;

    try {
      setProcessing(preApprovalId);
      setError("");

      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/visitors/pre-approved/${preApprovalId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${residentToken}` }
      });

      if (!response.ok) throw new Error("Failed to remove pre-approval");

      setSuccess("✅ Pre-approval removed");
      setTimeout(() => setSuccess(""), 3000);

      loadVisitors();
    } catch (err) {
      setError("Error removing pre-approval: " + err.message);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="resident-layout">
        <div className="resident-content">
          <div className="visitor-approvals-container">
            <div className="loading">Loading visitor approvals...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="resident-layout">
      <div className="resident-content">
        <div className="visitor-approvals-container">
          <div className="approvals-header">
            <h1>🚪 Visitor Approvals</h1>
            <p>Manage visitor entries and approvals</p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card pending">
              <div className="card-label">Pending Approvals</div>
              <div className="card-value">{pendingVisitors.length}</div>
            </div>
            <div className="summary-card pre-approved">
              <div className="card-label">Pre-Approved Visitors</div>
              <div className="card-value">{preApprovedVisitors.length}</div>
            </div>
            <div className="summary-card completed">
              <div className="card-label">Total Visits (30 days)</div>
              <div className="card-value">{visitorHistory.length}</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button
              className={`tab ${activeTab === "pending" ? "active" : ""}`}
              onClick={() => setActiveTab("pending")}
            >
              ⏳ Pending ({pendingVisitors.length})
            </button>
            <button
              className={`tab ${activeTab === "pre-approved" ? "active" : ""}`}
              onClick={() => setActiveTab("pre-approved")}
            >
              ⭐ Pre-Approved ({preApprovedVisitors.length})
            </button>
            <button
              className={`tab ${activeTab === "history" ? "active" : ""}`}
              onClick={() => setActiveTab("history")}
            >
              📋 History ({visitorHistory.length})
            </button>
          </div>

          {/* Pending Approvals */}
          {activeTab === "pending" && (
            <div className="visitors-list">
              {pendingVisitors.length === 0 ? (
                <div className="empty-state">
                  <p>✅ No pending visitor approvals</p>
                  <p className="small">All visitors have been reviewed</p>
                </div>
              ) : (
                pendingVisitors.map((visitor) => (
                  <div key={visitor.id} className="visitor-card pending">
                    <div className="visitor-header">
                      <div className="visitor-info">
                        <h3>{visitor.visitorName}</h3>
                        <span className="visitor-type">
                          {visitor.visitorType === "vendor" ? "🏢 Vendor" : "👤 Guest"}
                        </span>
                      </div>
                      <div className="visitor-time">
                        <span className="check-in">
                          ✓ {formatDate(visitor.checkInTime)}
                        </span>
                      </div>
                    </div>

                    <div className="visitor-details">
                      <div className="detail-row">
                        <span className="label">Phone:</span>
                        <span className="value">{visitor.visitorPhone || "N/A"}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Purpose:</span>
                        <span className="value">{visitor.purpose || "Not specified"}</span>
                      </div>

                      {visitor.visitorType === "vendor" && visitor.vendorCompany && (
                        <>
                          <div className="detail-row">
                            <span className="label">Company:</span>
                            <span className="value">{visitor.vendorCompany}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Category:</span>
                            <span className="value">{visitor.vendorCategory || "N/A"}</span>
                          </div>
                        </>
                      )}

                      <div className="detail-row">
                        <span className="label">Vehicle:</span>
                        <span className="value">{visitor.vehicleNumber || "None"}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Expected Duration:</span>
                        <span className="value">{visitor.expectedDuration}</span>
                      </div>
                    </div>

                    <div className="approval-section">
                      <div className="notes-input">
                        <label>Approval Notes (Optional)</label>
                        <textarea
                          placeholder="Add any notes..."
                          value={approvalNotes[visitor.id] || ""}
                          onChange={(e) =>
                            setApprovalNotes({
                              ...approvalNotes,
                              [visitor.id]: e.target.value
                            })
                          }
                          className="notes-textarea"
                        />
                      </div>

                      <div className="approval-buttons">
                        <button
                          className="btn-approve"
                          onClick={() => handleApprove(visitor.id)}
                          disabled={processing === visitor.id}
                        >
                          {processing === visitor.id ? "Processing..." : "✅ Approve"}
                        </button>
                        <button
                          className="btn-reject"
                          onClick={async () => {
                            const reason = await confirm({
                              title: "Reject visitor",
                              message: `Reject ${visitor.visitorName}'s entry request?`,
                              input: {
                                label: "Reason",
                                placeholder: "e.g. Not expecting anyone today",
                                required: true,
                              },
                              confirmLabel: "Reject",
                              danger: true,
                            });
                            if (!reason) return; // cancelled
                            setRejectionReasons({ ...rejectionReasons, [visitor.id]: reason });
                            handleReject(visitor.id);
                          }}
                          disabled={processing === visitor.id}
                        >
                          {processing === visitor.id ? "Processing..." : "❌ Reject"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Pre-Approved Visitors */}
          {activeTab === "pre-approved" && (
            <div className="pre-approvals-section">
              {!showPreApprovalForm ? (
                <button
                  className="btn-add-pre-approval"
                  onClick={() => setShowPreApprovalForm(true)}
                >
                  + Pre-Approve a Visitor
                </button>
              ) : (
                <form onSubmit={handleAddPreApproval} className="pre-approval-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="visitorName">Visitor Name *</label>
                      <input
                        type="text"
                        id="visitorName"
                        value={preApprovalForm.visitorName}
                        onChange={(e) =>
                          setPreApprovalForm({ ...preApprovalForm, visitorName: e.target.value })
                        }
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="visitorPhone">Phone</label>
                      <input
                        type="tel"
                        id="visitorPhone"
                        value={preApprovalForm.visitorPhone}
                        onChange={(e) =>
                          setPreApprovalForm({ ...preApprovalForm, visitorPhone: e.target.value })
                        }
                        placeholder="Phone number"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="visitorEmail">Email</label>
                      <input
                        type="email"
                        id="visitorEmail"
                        value={preApprovalForm.visitorEmail}
                        onChange={(e) =>
                          setPreApprovalForm({ ...preApprovalForm, visitorEmail: e.target.value })
                        }
                        placeholder="Email address"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="purpose">Purpose</label>
                      <input
                        type="text"
                        id="purpose"
                        value={preApprovalForm.purpose}
                        onChange={(e) =>
                          setPreApprovalForm({ ...preApprovalForm, purpose: e.target.value })
                        }
                        placeholder="Visit purpose"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="expectedDate">Expected Date *</label>
                      <input
                        type="date"
                        id="expectedDate"
                        value={preApprovalForm.expectedDate}
                        onChange={(e) =>
                          setPreApprovalForm({ ...preApprovalForm, expectedDate: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="validUntilDate">Valid Until (Optional)</label>
                      <input
                        type="date"
                        id="validUntilDate"
                        value={preApprovalForm.validUntilDate}
                        onChange={(e) =>
                          setPreApprovalForm({ ...preApprovalForm, validUntilDate: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-save"
                      disabled={processing === "adding"}
                    >
                      {processing === "adding" ? "Adding..." : "✅ Add Pre-Approval"}
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => setShowPreApprovalForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <div className="visitors-list">
                {preApprovedVisitors.length === 0 ? (
                  <div className="empty-state">
                    <p>No pre-approved visitors</p>
                    <p className="small">Pre-approve visitors so they can be quickly checked in at the gate</p>
                  </div>
                ) : (
                  preApprovedVisitors.map((visitor) => (
                    <div key={visitor.id} className="visitor-card pre-approved">
                      <div className="visitor-header">
                        <div className="visitor-info">
                          <h3>{visitor.visitorName}</h3>
                          <span className="status-badge">⭐ Pre-Approved</span>
                        </div>
                        <div className="visitor-dates">
                          <span className="date-label">Expected: {formatDateOnly(visitor.expectedDate)}</span>
                          {visitor.validUntilDate && (
                            <span className="date-label">Until: {formatDateOnly(visitor.validUntilDate)}</span>
                          )}
                        </div>
                      </div>

                      <div className="visitor-details">
                        {visitor.visitorPhone && (
                          <div className="detail-row">
                            <span className="label">Phone:</span>
                            <span className="value">{visitor.visitorPhone}</span>
                          </div>
                        )}
                        {visitor.visitorEmail && (
                          <div className="detail-row">
                            <span className="label">Email:</span>
                            <span className="value">{visitor.visitorEmail}</span>
                          </div>
                        )}
                        {visitor.purpose && (
                          <div className="detail-row">
                            <span className="label">Purpose:</span>
                            <span className="value">{visitor.purpose}</span>
                          </div>
                        )}
                      </div>

                      <div className="visitor-actions">
                        <button
                          className="btn-remove"
                          onClick={() => handleRemovePreApproval(visitor.id)}
                          disabled={processing === visitor.id}
                        >
                          {processing === visitor.id ? "Removing..." : "🗑️ Remove"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Visitor History */}
          {activeTab === "history" && (
            <div className="visitors-list">
              {visitorHistory.length === 0 ? (
                <div className="empty-state">
                  <p>No visitor history yet</p>
                </div>
              ) : (
                visitorHistory.map((visitor) => (
                  <div
                    key={visitor.id}
                    className={`visitor-card ${
                      visitor.status === "approved"
                        ? "approved"
                        : visitor.status === "rejected"
                        ? "rejected"
                        : "completed"
                    }`}
                  >
                    <div className="visitor-header">
                      <div className="visitor-info">
                        <h3>{visitor.visitorName}</h3>
                        <span className="visitor-type">
                          {visitor.visitorType === "vendor" ? "🏢 Vendor" : "👤 Guest"}
                        </span>
                      </div>
                      <div className="visitor-status">
                        {visitor.status === "approved" && (
                          <span className="status approved">✅ APPROVED</span>
                        )}
                        {visitor.status === "rejected" && (
                          <span className="status rejected">❌ REJECTED</span>
                        )}
                        {visitor.status === "completed" && (
                          <span className="status completed">✓ COMPLETED</span>
                        )}
                      </div>
                    </div>

                    <div className="visitor-details">
                      <div className="detail-row">
                        <span className="label">Phone:</span>
                        <span className="value">{visitor.visitorPhone || "N/A"}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Purpose:</span>
                        <span className="value">{visitor.purpose || "Not specified"}</span>
                      </div>

                      {visitor.visitorType === "vendor" && visitor.vendorCompany && (
                        <div className="detail-row">
                          <span className="label">Company:</span>
                          <span className="value">{visitor.vendorCompany}</span>
                        </div>
                      )}

                      <div className="detail-row">
                        <span className="label">Check-in:</span>
                        <span className="value">{formatDate(visitor.checkInTime)}</span>
                      </div>

                      {visitor.checkOutTime && (
                        <>
                          <div className="detail-row">
                            <span className="label">Check-out:</span>
                            <span className="value">{formatDate(visitor.checkOutTime)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Duration:</span>
                            <span className="value">
                              {formatDuration(visitor.checkInTime, visitor.checkOutTime)}
                            </span>
                          </div>
                        </>
                      )}

                      {visitor.approvalNotes && (
                        <div className="detail-row">
                          <span className="label">Notes:</span>
                          <span className="value">{visitor.approvalNotes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}